/**
 * Adapters between the agreement texts in `data/contract-documents.ts` and the
 * Ask engine: document lists, plus a citation helper that points at a section
 * of an agreement.
 */
import { getContractDocument } from '../../data/contract-documents';
import type { ContractDocument } from '../../data/contract-documents';
import {
  CONTRACT_LIST,
  childContracts,
  contractKindLabel,
  getContract,
} from '../../data/contracts';
import { getSupplier } from '../../data/suppliers';
import type { ContractId } from '../../data/types';
import type { AskCitation, AskDocument } from '../../ask/types';

/** "Gently master agreement", "Corvane supply agreement", "Halbrook statement of work". */
export function shortTitleFor(contractId: ContractId): string {
  const contract = getContract(contractId);
  const kind = contractKindLabel(contract.kind).toLowerCase();
  return `${getSupplier(contract.supplierId).shortName} ${kind}`;
}

function toAskDocument(
  contractId: ContractId,
  doc: ContractDocument,
): AskDocument {
  return {
    id: contractId,
    title: `${doc.title} (${contractId})`,
    shortTitle: shortTitleFor(contractId),
    sections: doc.sections.map((section) => ({
      ref: section.number,
      heading: section.heading,
      text: section.paragraphs.join('\n\n'),
    })),
  };
}

const cache = new Map<string, AskDocument>();

function askDocumentFor(contractId: ContractId): AskDocument | undefined {
  const cached = cache.get(contractId);
  if (cached) return cached;
  const doc = getContractDocument(contractId);
  if (!doc) return undefined;
  const built = toAskDocument(contractId, doc);
  cache.set(contractId, built);
  return built;
}

/** Every agreement text, to check that the repository answers cite real sections. */
export function allAskDocuments(): AskDocument[] {
  return CONTRACT_LIST.flatMap((contract) => {
    const doc = askDocumentFor(contract.id);
    return doc ? [doc] : [];
  });
}

/** The agreement, its parent and its children: what a free-text question on its page reads. */
export function askDocumentsForContract(contractId: ContractId): AskDocument[] {
  const contract = getContract(contractId);
  const ids: ContractId[] = [contractId];
  if (contract.parentId) ids.push(contract.parentId);
  for (const child of childContracts(contractId)) ids.push(child.id);
  return ids.flatMap((id) => {
    const doc = askDocumentFor(id);
    return doc ? [doc] : [];
  });
}

/**
 * Citation for a section of an agreement. Authored answers cite by
 * `{ documentId, ref }` only and never copy agreement wording, so they stay
 * correct when the text is edited. `dataQuote` is for an excerpt read from the
 * clause database (never hand-typed); it is attached only when it really
 * appears in the cited section.
 */
export function cite(
  contractId: ContractId,
  ref: string,
  dataQuote?: string,
): AskCitation {
  const section = getContractDocument(contractId)?.sections.find(
    (entry) => entry.number === ref,
  );
  const text = section ? section.paragraphs.join('\n\n') : '';
  const quote = dataQuote && text.includes(dataQuote) ? dataQuote : undefined;
  return { documentId: contractId, ref, quote };
}
